import { Product, SearchProductEntity } from '@generated-client/backend';
import { ScrollShadow } from '@heroui/react';
import { Path } from '@trwl/shared/lib/router';
import { PageDto } from '@trwl/shared/types';
import { SliderArrow } from '@trwl/shared/ui/icons';
import { setDuration, setShow } from '@trwl/store';
import { serializeFilterToQuery } from '@trwl/store/filter/serializer';
import { capitalizeText } from '@trwl/util';
import { preprocessImage } from '@trwl/util/preprocessImage';
import cn from 'classnames';
import { useUnit } from 'effector-react';
import Image from 'next/image';
import Link from 'next/link';
import React, { useCallback, useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { ItemList, WithContext } from 'schema-dts';
const IMAGE_SIZE = 200;

export const RelationProductsSlider = ({
  relationProducts,
  product,
  size = 65,
}: {
  product: Product;
  relationProducts: PageDto<SearchProductEntity>;
  size?: number;
}) => {
  const [changeShow, changeDuration] = useUnit([setShow, setDuration]);
  const onProductClick = useCallback(() => {
    changeDuration(20000);
    changeShow(true);
  }, [changeShow, changeDuration]);

  const moreLink = useMemo(() => {
    if (!relationProducts.content[0]?.seriesId) return Path.CATALOG;
    return `${Path.CATALOG}?filter=${serializeFilterToQuery({
      brandIds: relationProducts.content[0].brandId
        ? [relationProducts.content[0].brandId]
        : undefined,
      seriesIds: [relationProducts.content[0].seriesId],
    })}`;
  }, [relationProducts.content]);

  const moreProductsCount =
    relationProducts.totalElements - relationProducts.content.length;

  if (!relationProducts.totalElements) return null;

  const jsonLd: WithContext<ItemList> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'itemListElement': relationProducts.content.map((relationProduct, idx) => ({
      '@type': 'ListItem',
      'position': idx + 1,
      'item': {
        '@type': 'Product',
        'name': capitalizeText(relationProduct.title),
        'image': preprocessImage(relationProduct.logo ?? '', IMAGE_SIZE),
        'url': `https://www.trwl.store/product/${relationProduct.id}`,
        'sku': relationProduct.article,
        'category': product.category && capitalizeText(product.category.name),
        ...(product.series && { model: product.series.name }),
        ...(product.brand && {
          brand: {
            '@type': 'Brand',
            'name': product.brand.name,
            'url': `https://www.trwl.store/catalog?filter=${serializeFilterToQuery(
              {
                brandIds: [product.brand.id],
              },
            )}`,
          },
        }),
      },
    })),
  };

  return (
    <section
      className={'w-full h-full relative'}
      aria-label="Связанные товары"
      role="region"
    >
      <h2 className="sr-only">Связанные товары</h2>
      <ScrollShadow
        size={0}
        orientation={'horizontal'}
        hideScrollBar
        className={'overflow-y-hidden'}
      >
        <ul
          className="flex flex-row gap-[8px]"
          aria-label="Список связанных товаров"
        >
          {relationProducts.content.map((productEntity) => (
            <li
              key={productEntity.id}
              className={cn(
                'transition rounded-[4px] list-none overflow-hidden',
                !isMobile && 'hover:scale-[1.03] duration-300',
              )}
              style={{
                height: size,
                minHeight: size,
                maxHeight: size,
                width: size,
                minWidth: size,
                maxWidth: size,
              }}
            >
              <Link
                href={`${Path.PRODUCT}/${productEntity.id}`}
                onClick={onProductClick}
                aria-label={`Подробнее о товаре: ${productEntity.title}`}
              >
                <Image
                  className={'p-0.5'}
                  src={preprocessImage(productEntity?.logo ?? '', IMAGE_SIZE)}
                  width={IMAGE_SIZE}
                  height={IMAGE_SIZE}
                  alt={productEntity?.title}
                  loading="lazy"
                />
              </Link>
            </li>
          ))}
        </ul>
        {moreProductsCount > 0 && (
          <Link
            className={cn(
              'absolute top-0 right-0',
              'flex items-center justify-center gap-2',
              'bg-white md:hover:bg-[#F2F2F2] duration-300 cursor-pointer rounded-[10px]',
              'text-[17px] lg:text-[24px] font-light',
            )}
            style={{ width: size, height: size }}
            href={moreLink}
            aria-label={`Показать еще ${moreProductsCount} товаров`}
          >
            <span className={'pt-0.5'}>+{moreProductsCount}</span>
            <SliderArrow className="pointer-events-none" />
          </Link>
        )}
      </ScrollShadow>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </section>
  );
};
